import JSZip from "jszip";
import type { EmbeddingBackendInfo, MarkerEmbeddingsFile } from "@ndp61/cms-shared";
import { FIELD_CAPTURE_DEFAULT_HOLD_MS } from "../constants";

export interface ZipInput {
  poiId: string;
  gps: { lat: number; lng: number; accuracyM: number; detectionRadiusM: number; source: string };
  embeddings: MarkerEmbeddingsFile;
  photos: Blob[];
  heroPhoto?: Blob;
  missionPhoto?: Blob;
  threshold?: number;
  holdMs?: number;
  verificationResult?: unknown;
  backendInfo?: EmbeddingBackendInfo;
  notes?: string;
}

export async function assembleZip(input: ZipInput): Promise<Blob> {
  const zip = new JSZip();

  const manifest = {
    poiId: input.poiId,
    schemaVersion: "1.0",
    capturedAt: new Date().toISOString(),
    userAgent: navigator.userAgent,
    appVersion: "1.0.0",
  };
  zip.file("manifest.json", JSON.stringify(manifest, null, 2));
  zip.file("gps.json", JSON.stringify(input.gps, null, 2));

  if (input.heroPhoto) {
    zip.file("hero.jpg", input.heroPhoto);
  }
  if (input.missionPhoto) {
    zip.file("mission.jpg", input.missionPhoto);
  }

  const recognition = zip.folder("recognition")!;
  recognition.file("embeddings.json", JSON.stringify(input.embeddings, null, 2));

  const photosFolder = recognition.folder("photos")!;
  const photosIndex: { filename: string; index: number }[] = [];
  for (let i = 0; i < input.photos.length; i++) {
    const filename = String(i + 1).padStart(2, "0") + ".jpg";
    photosFolder.file(filename, input.photos[i]);
    photosIndex.push({ filename, index: i });
  }
  photosFolder.file("photos.json", JSON.stringify(photosIndex, null, 2));

  const threshold = input.threshold ?? 0.65;
  const holdMs = input.holdMs ?? FIELD_CAPTURE_DEFAULT_HOLD_MS;
  recognition.file("tuning.json", JSON.stringify({ threshold, holdMs }, null, 2));

  if (input.verificationResult !== undefined) {
    zip.file("verification.json", JSON.stringify(input.verificationResult, null, 2));
  }

  recognition.file("debug.json", JSON.stringify({
    backend: input.backendInfo?.backend ?? null,
    gpu: input.backendInfo?.gpu ?? null,
    maliOverride: input.backendInfo?.maliOverride ?? null,
    iosDetected: input.backendInfo?.iosDetected ?? null,
    iosOverride: input.backendInfo?.iosOverride ?? null,
    capturedAt: input.backendInfo?.capturedAt ?? null,
  }, null, 2));

  if (input.notes && input.notes.trim().length > 0) {
    zip.file("notes.txt", input.notes);
  }

  return zip.generateAsync({ type: "blob" });
}
